import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { AlertCircle, ArrowRight } from "lucide-react";

export function OutstandingCustomers() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["outstanding-customers"],
    queryFn: async () => (await api.get("/dashboard/outstanding-customers?limit=8")).data,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="rounded-3xl border border-pawkt-border bg-white p-5 sm:p-6 shadow-card" data-testid="outstanding-customers">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-extrabold text-pawkt-text">Money to Collect</h3>
          <p className="text-xs text-pawkt-text-secondary">
            {data ? <>Total outstanding <span className="font-bold text-pawkt-text">{inr(data.total_outstanding)}</span> · {data.customer_count} customer{data.customer_count === 1 ? "" : "s"}</> : "…"}
          </p>
        </div>
      </div>

      {isLoading ? (
        <p className="mt-6 text-sm text-slate-400">Loading…</p>
      ) : !(data?.items?.length) ? (
        <p className="mt-6 text-sm text-slate-400" data-testid="outstanding-empty">No overdue customers. You're all collected up.</p>
      ) : (
        <div className="mt-4 divide-y divide-slate-100" data-testid="outstanding-list">
          {data.items.map((r) => (
            <div key={r.customer_id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`outstanding-row-${r.customer_id}`}>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-pawkt-text" data-testid={`outstanding-name-${r.customer_id}`}>{r.customer_name || "Unknown customer"}</div>
                <div className="truncate text-xs text-slate-500">
                  {r.category || r.invoice_number || "Invoice"}
                  {" · "}
                  <span className={r.days_outstanding >= 30 ? "text-amber-600 font-semibold" : ""}>{r.days_outstanding} day{r.days_outstanding === 1 ? "" : "s"} outstanding</span>
                  {r.invoices > 1 ? ` · ${r.invoices} invoices` : ""}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm font-extrabold text-pawkt-text">{inr(r.balance)}</span>
                <button onClick={() => navigate(`/customers/${r.customer_id}`)} data-testid={`outstanding-view-${r.customer_id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-pawkt-elevated px-3 py-1.5 text-xs font-semibold text-pawkt-text-secondary hover:bg-slate-200">
                  View <ArrowRight className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
          {data.customer_count > data.items.length && (
            <button onClick={() => navigate("/invoices")} data-testid="outstanding-view-all"
              className="mt-2 flex w-full items-center justify-center gap-1 pt-3 text-xs font-semibold text-pawkt-primary-deep hover:underline">
              View all {data.customer_count} outstanding <ArrowRight className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
